
import { useEffect, useState } from 'react';
import { getOfferPageData, postOfferPageData } from '../../../api/apiServices';

export default function OfferEditor() {
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  const [offerData, setOfferData] = useState({
    heroTitle: 'Get More Clients',
    heroSubtitle: 'With a Professional',
    heroHighlight: 'Law Firm Website',
    heroDescription: 'Stop losing potential clients...',
    trustBadge: 'Trusted by 200+ Law Firms Nationwide',
    originalPrice: 6000,
    discountedPrice: 3997,
    discount: 2000,
    urgencyText: 'Only 5 spots available this month',
    problemTitle: 'Is Your Current Website Costing You Clients?',
    problemSubtitle: 'Most law firm websites fail to convert...',
    solutionTitle: 'The Complete Solution for Law Firms',
    solutionSubtitle: 'Get a professional website...',
    resultsTitle: 'Real Results from Real Law Firms',
    resultsSubtitle: 'See how our websites have transformed...',
    processTitle: 'Our Proven Process',
    processSubtitle: 'From consultation to launch in just 14 days',
    formTitle: 'Get Your Free Website Audit',
    formSubtitle: 'Discover exactly what’s holding your current website back...',
    faqTitle: 'Frequently Asked Questions',
    finalCtaTitle: 'Ready to Transform Your Law Practice?',
    finalCtaSubtitle: 'Join 200+ law firms who’ve already upgraded...',
    phone: '+1 (555) 123-4567',
    email: 'hello@lawfirmwebsites.com',
  });

  const [problems, setProblems] = useState([]);
  const [solutions, setSolutions] = useState([]);
  const [stats, setStats] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [processSteps, setProcessSteps] = useState([]);
  const [faqs, setFaqs] = useState([]);

   // ✅ Fetch backend data on mount
  useEffect(() => {
    const fetchOfferData = async () => {
      try {
        const data = await getOfferPageData();

        if (data) {
          // Prefill only if backend has data
          setOfferData(prev => ({
            ...prev,
            heroTitle: data.heroTitle || prev.heroTitle,
            heroSubtitle: data.heroSubtitle || prev.heroSubtitle,
            heroHighlight: data.heroHighlight || prev.heroHighlight,
            heroDescription: data.heroDescription || prev.heroDescription,
            trustBadge: data.trustBadge || prev.trustBadge,
            problemTitle: data.problemSectionTitle || prev.problemTitle,
            problemSubtitle: data.problemSectionSubtitle || prev.problemSubtitle,
            solutionTitle: data.solutionSectionTitle || prev.solutionTitle,
            solutionSubtitle: data.solutionSectionSubtitle || prev.solutionSubtitle,
            resultsTitle: data.resultsSectionTitle || prev.resultsTitle,
            resultsSubtitle: data.resultsSectionSubtitle || prev.resultsSubtitle,
            processTitle: data.processSectionTitle || prev.processTitle,
            processSubtitle: data.processSectionSubtitle || prev.processSubtitle,
            formTitle: data.formTitle || prev.formTitle,
            formSubtitle: data.formSubtitle || prev.formSubtitle,
            faqTitle: data.faqSectionTitle || prev.faqTitle,
            finalCtaTitle: data.finalCtaTitle || prev.finalCtaTitle,
            finalCtaSubtitle: data.finalCtaSubtitle || prev.finalCtaSubtitle,
            phone: data.phoneNumber || prev.phone,
            email: data.emailAddress || prev.email,
          }));

          if (data.problemPoints?.length) setProblems(data.problemPoints);
          if (data.solutionFeatures?.length) setSolutions(data.solutionFeatures);
          if (data.statistics?.length) setStats(data.statistics.map(s => ({ number: s.value, label: s.label })));
          if (data.clientTestimonials?.length)
            setTestimonials(
              data.clientTestimonials.map(t => ({
                name: t.clientName,
                firm: t.company,
                location: t.position,
                quote: t.testimonialContent,
                image: t.avatarImage,
                result: t.result,
              }))
            );
          if (data.processSteps?.length)
            setProcessSteps(
              data.processSteps.map((step, i) => ({
                step: String(step.stepNumber || i + 1).padStart(2, "0"),
                title: step.title,
                description: step.description,
                duration: step.duration || "",
              }))
            );
          if (data.faqItems?.length) setFaqs(data.faqItems);
        }
      } catch (error) {
        console.error("Failed to fetch offer page data:", error);
      }
    };

    fetchOfferData();
  }, []);

  const handleInputChange = (field, value) => {
    setOfferData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProblemChange = (index, field, value) => {
    setProblems(prev => prev.map((problem, i) => 
      i === index ? { ...problem, [field]: value } : problem
    ));
  };

  const handleSolutionChange = (index, field, value) => {
    setSolutions(prev => prev.map((solution, i) => 
      i === index ? { ...solution, [field]: value } : solution
    ));
  };

  const handleStatChange = (index, field, value) => {
    setStats(prev => prev.map((stat, i) => 
      i === index ? { ...stat, [field]: value } : stat
    ));
  };

  const handleTestimonialChange = (index, field, value) => {
    setTestimonials(prev => prev.map((testimonial, i) => 
      i === index ? { ...testimonial, [field]: value } : testimonial
    ));
  };


  const handleProcessStepChange = (index, field, value) => {
    setProcessSteps(prev => prev.map((step, i) => 
      i === index ? { ...step, [field]: value } : step
    ));
  };

  const handleFaqChange = (index, field, value) => {
    setFaqs(prev => prev.map((faq, i) => 
      i === index ? { ...faq, [field]: value } : faq
    ));
  };

  const addProblem = () => {
    setProblems(prev => [...prev, {
      icon: 'ri-alert-line',
      title: 'New Problem',
      description: 'Description of the problem...',
      stat: 'Statistic'
    }]);
  };

  const removeProblem = (index) => {
    setProblems(prev => prev.filter((_, i) => i !== index));
  };

  const addSolution = () => {
    setSolutions(prev => [...prev, {
      icon: 'ri-check-line',
      title: 'New Solution',
      description: 'Description of the solution...'
    }]);
  };

  const removeSolution = (index) => {
    setSolutions(prev => prev.filter((_, i) => i !== index));
  };

  const addStat = () => {
    setStats(prev => [...prev, {
      number: '100%',
      label: 'New Statistic'
    }]);
  };

  const removeStat = (index) => {
    setStats(prev => prev.filter((_, i) => i !== index));
  };

  const addTestimonial = () => {
    setTestimonials(prev => [...prev, {
      name: 'Client Name',
      firm: 'Law Firm Name',
      location: 'City, State',
      quote: 'Client testimonial quote...',
      image: 'Client testimonial image...',
      result: 'Result achieved'
    }]);
  };

  const removeTestimonial = (index) => {
    setTestimonials(prev => prev.filter((_, i) => i !== index));
  };

  const addProcessStep = () => {
    const stepNumber = String(processSteps.length + 1).padStart(2, '0');
    setProcessSteps(prev => [...prev, {
      step: stepNumber,
      title: 'New Step',
      description: 'Description of this step...',
      duration: 'Timeline'
    }]);
  };

  const removeProcessStep = (index) => {
    setProcessSteps(prev => prev.filter((_, i) => i !== index));
  };

  const addFaq = () => {
    setFaqs(prev => [...prev, {
      question: 'New question?',
      answer: 'Answer to the question...'
    }]);
  };

  const removeFaq = (index) => {
    setFaqs(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveStatus('');
    
    try {
      // Transform the current state to match the API format
      const formData = {
  trustBadge: offerData.trustBadge,
  heroTitle: offerData.heroTitle,
  heroSubtitle: offerData.heroSubtitle,
  heroHighlight: offerData.heroHighlight,
  heroDescription: offerData.heroDescription,

  problemSectionTitle: offerData.problemTitle,
  problemSectionSubtitle: offerData.problemSubtitle,
  solutionSectionTitle: offerData.solutionTitle,
  solutionSectionSubtitle: offerData.solutionSubtitle,

  formTitle: offerData.formTitle,
  formSubtitle: offerData.formSubtitle,
  faqSectionTitle: offerData.faqTitle,
  finalCtaTitle: offerData.finalCtaTitle,
  finalCtaSubtitle: offerData.finalCtaSubtitle,

  originalPrice: parseInt(offerData.originalPrice) || 0,
  discountedPrice: parseInt(offerData.discountedPrice) || 0,
  discountAmount: offerData.discount,
  urgencyText: offerData.urgencyText,

  resultsSectionTitle: offerData.resultsTitle,
  resultsSectionSubtitle: offerData.resultsSubtitle,
  processSectionTitle: offerData.processTitle,
  processSectionSubtitle: offerData.processSubtitle,

  phoneNumber: offerData.phone,
  emailAddress: offerData.email,

  // ✅ FIXED: send objects, not strings
  problemPoints: problems.map(p => ({
    icon: p.icon,
    title: p.title,
    description: p.description,
    stat: p.stat
  })),

  solutionFeatures: solutions.map(s => ({
    icon: s.icon,
    title: s.title,
    description: s.description
  })),

  statistics: stats.map(s => ({
    label: s.label,
    value: s.number
  })),

  clientTestimonials: testimonials.map(t => ({
    clientName: t.name,
    position: t.location,
    company: t.firm,
    rating: 5,
    avatarImage: t.image,
    testimonialContent: t.quote,
    result: t.result
  })),

  processSteps: processSteps.map((step, index) => ({
    stepNumber: index + 1,
    title: step.title,
    description: step.description
  })),

  faqItems: faqs.map(faq => ({
    question: faq.question,
    answer: faq.answer
  }))
};


      await postOfferPageData(formData);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Failed to save offer page data:', error);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <i className="ri-scales-line text-2xl text-blue-600"></i>
          <h1 className="text-2xl font-bold text-gray-900">Law Firm Offer Page Editor</h1>
        </div>
        
        <div className="flex items-center gap-3">
          {saveStatus === 'success' && (
            <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-2 rounded-lg">
              <i className="ri-check-line"></i>
              <span className="text-sm font-medium">Saved successfully!</span>
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="flex items-center gap-2 text-red-600 bg-red-50 px-3 py-2 rounded-lg">
              <i className="ri-error-warning-line"></i>
              <span className="text-sm font-medium">Save failed. Please try again.</span>
            </div>
          )}
          
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSaving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <i className="ri-save-line"></i>
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </div>

      <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center">
          <i className="ri-scales-3-line mr-3 text-emerald-600"></i>
          Law Firm Offer Page Settings
        </h2>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Hero Section */}
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
            <i className="ri-home-line mr-2 text-emerald-600"></i>
            Hero Section
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Trust Badge</label>
              <input
                type="text"
                value={offerData.trustBadge}
                onChange={(e) => handleInputChange('trustBadge', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Trusted by 200+ Law Firms Nationwide"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Hero Title</label>
              <input
                type="text"
                value={offerData.heroTitle}
                onChange={(e) => handleInputChange('heroTitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Get More Clients"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Hero Subtitle</label>
              <input
                type="text"
                value={offerData.heroSubtitle}
                onChange={(e) => handleInputChange('heroSubtitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="With a Professional"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Hero Highlight</label>
              <input
                type="text"
                value={offerData.heroHighlight}
                onChange={(e) => handleInputChange('heroHighlight', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Law Firm Website"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Hero Description</label>
              <textarea
                value={offerData.heroDescription}
                onChange={(e) => handleInputChange('heroDescription', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
                placeholder="Hero description..."
              />
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
            <i className="ri-price-tag-3-line mr-2 text-emerald-600"></i>
            Pricing & Offer
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Original Price</label>
              <input
                type="text"
                value={offerData.originalPrice}
                onChange={(e) => handleInputChange('originalPrice', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="$6,000"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Discounted Price</label>
              <input
                type="text"
                value={offerData.discountedPrice}
                onChange={(e) => handleInputChange('discountedPrice', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="$3,997"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Discount Amount</label>
              <input
                type="text"
                value={offerData.discount}
                onChange={(e) => handleInputChange('discount', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="$2,000"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Urgency Text</label>
              <input
                type="text"
                value={offerData.urgencyText}
                onChange={(e) => handleInputChange('urgencyText', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Only 5 spots available this month"
              />
            </div>
          </div>
        </div>

        {/* Section Titles */}
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
            <i className="ri-text mr-2 text-emerald-600"></i>
            Section Titles
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Problem Section Title</label>
              <input
                type="text"
                value={offerData.problemTitle}
                onChange={(e) => handleInputChange('problemTitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Is Your Current Website Costing You Clients?"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Problem Section Subtitle</label>
              <textarea
                value={offerData.problemSubtitle}
                onChange={(e) => handleInputChange('problemSubtitle', e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
                placeholder="Problem subtitle..."
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Solution Section Title</label>
              <input
                type="text"
                value={offerData.solutionTitle}
                onChange={(e) => handleInputChange('solutionTitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="The Complete Solution for Law Firms"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Solution Section Subtitle</label>
              <textarea
                value={offerData.solutionSubtitle}
                onChange={(e) => handleInputChange('solutionSubtitle', e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
                placeholder="Solution subtitle..."
              />
            </div>
          </div>
        </div>

        {/* More Section Titles */}
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
            <i className="ri-article-line mr-2 text-emerald-600"></i>
            More Section Titles
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Results Section Title</label>
              <input
                type="text"
                value={offerData.resultsTitle}
                onChange={(e) => handleInputChange('resultsTitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Real Results from Real Law Firms"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Results Section Subtitle</label>
              <input
                type="text"
                value={offerData.resultsSubtitle}
                onChange={(e) => handleInputChange('resultsSubtitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="See how our websites have transformed law practices across the USA"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Process Section Title</label>
              <input
                type="text"
                value={offerData.processTitle}
                onChange={(e) => handleInputChange('processTitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Our Proven Process"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Process Section Subtitle</label>
              <input
                type="text"
                value={offerData.processSubtitle}
                onChange={(e) => handleInputChange('processSubtitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="From consultation to launch in just 14 days"
              />
            </div>
          </div>
        </div>

        {/* Form & CTA Section */}
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
            <i className="ri-file-list-3-line mr-2 text-emerald-600"></i>
            Form & CTA Section
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Form Title</label>
              <input
                type="text"
                value={offerData.formTitle}
                onChange={(e) => handleInputChange('formTitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Get Your Free Website Audit"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Form Subtitle</label>
              <textarea
                value={offerData.formSubtitle}
                onChange={(e) => handleInputChange('formSubtitle', e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
                placeholder="Form subtitle..."
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">FAQ Section Title</label>
              <input
                type="text"
                value={offerData.faqTitle}
                onChange={(e) => handleInputChange('faqTitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Frequently Asked Questions"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Final CTA Title</label>
              <input
                type="text"
                value={offerData.finalCtaTitle}
                onChange={(e) => handleInputChange('finalCtaTitle', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="Ready to Transform Your Law Practice?"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Final CTA Subtitle</label>
              <textarea
                value={offerData.finalCtaSubtitle}
                onChange={(e) => handleInputChange('finalCtaSubtitle', e.target.value)}
                rows={2}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300 resize-none"
                placeholder="Final CTA subtitle..."
              />
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center">
            <i className="ri-contacts-line mr-2 text-emerald-600"></i>
            Contact Information
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Phone Number</label>
              <input
                type="tel"
                value={offerData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-slate-700 font-semibold mb-2">Email Address</label>
              <input
                type="email"
                value={offerData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all duration-300"
                placeholder="hello@lawfirmwebsites.com"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Problem Points Editor */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800 flex items-center">
            <i className="ri-alert-line mr-2 text-red-500"></i>
            Problem Points ({problems.length} items)
          </h3>
          <button
            onClick={addProblem}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-300 flex items-center whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            Add Problem
          </button>
        </div>
        <div className="space-y-6">
          {problems.map((problem, index) => (
            <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-700">Problem {index + 1}</h4>
                <button
                  onClick={() => removeProblem(index)}
                  className="text-red-500 hover:text-red-700 transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Icon (Remix Icon class)</label>
                  <input
                    type="text"
                    value={problem.icon}
                    onChange={(e) => handleProblemChange(index, 'icon', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="ri-smartphone-line"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Statistic</label>
                  <input
                    type="text"
                    value={problem.stat}
                    onChange={(e) => handleProblemChange(index, 'stat', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="73% Mobile Traffic"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Title</label>
                  <input
                    type="text"
                    value={problem.title}
                    onChange={(e) => handleProblemChange(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Problem title"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Description</label>
                  <textarea
                    value={problem.description}
                    onChange={(e) => handleProblemChange(index, 'description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    placeholder="Problem description..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Solution Features Editor */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800 flex items-center">
            <i className="ri-check-line mr-2 text-emerald-500"></i>
            Solution Features ({solutions.length} items)
          </h3>
          <button
            onClick={addSolution}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-300 flex items-center whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            Add Solution
          </button>
        </div>
        <div className="space-y-6">
          {solutions.map((solution, index) => (
            <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-700">Solution {index + 1}</h4>
                <button
                  onClick={() => removeSolution(index)}
                  className="text-red-500 hover:text-red-700 transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Icon (Remix Icon class)</label>
                  <input
                    type="text"
                    value={solution.icon}
                    onChange={(e) => handleSolutionChange(index, 'icon', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="ri-palette-line"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Title</label>
                  <input
                    type="text"
                    value={solution.title}
                    onChange={(e) => handleSolutionChange(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Solution title"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-slate-600 font-medium mb-2">Description</label>
                  <textarea
                    value={solution.description}
                    onChange={(e) => handleSolutionChange(index, 'description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    placeholder="Solution description..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Statistics Editor */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800 flex items-center">
            <i className="ri-bar-chart-line mr-2 text-blue-500"></i>
            Statistics ({stats.length} items)
          </h3>
          <button
            onClick={addStat}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-300 flex items-center whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            Add Statistic
          </button>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {stats.map((stat, index) => (
            <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-700">Statistic {index + 1}</h4>
                <button
                  onClick={() => removeStat(index)}
                  className="text-red-500 hover:text-red-700 transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Number</label>
                  <input
                    type="text"
                    value={stat.number}
                    onChange={(e) => handleStatChange(index, 'number', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="340%"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Label</label>
                  <input
                    type="text"
                    value={stat.label}
                    onChange={(e) => handleStatChange(index, 'label', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Average Increase in Online Leads"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Client Testimonials Editor */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800 flex items-center">
            <i className="ri-chat-quote-line mr-2 text-purple-500"></i>
            Client Testimonials ({testimonials.length} items)
          </h3>
          <button
            onClick={addTestimonial}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-300 flex items-center whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            Add Testimonial
          </button>
        </div>
        <div className="space-y-6">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-700">Testimonial {index + 1}</h4>
                <button
                  onClick={() => removeTestimonial(index)}
                  className="text-red-500 hover:text-red-700 transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Client Name</label>
                  <input
                    type="text"
                    value={testimonial.name}
                    onChange={(e) => handleTestimonialChange(index, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Sarah Mitchell"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Law Firm</label>
                  <input
                    type="text"
                    value={testimonial.firm}
                    onChange={(e) => handleTestimonialChange(index, 'firm', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Mitchell Family Law"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Location</label>
                  <input
                    type="text"
                    value={testimonial.location}
                    onChange={(e) => handleTestimonialChange(index, 'location', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Austin, TX"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Result</label>
                  <input
                    type="text"
                    value={testimonial.result}
                    onChange={(e) => handleTestimonialChange(index, 'result', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="+280% Lead Increase"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-slate-600 font-medium mb-2">Image</label>
                  <textarea
                    value={testimonial.avatarImage}
                    onChange={(e) => handleTestimonialChange(index, 'image', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    placeholder="Client testimonial image..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-slate-600 font-medium mb-2">Quote</label>
                  <textarea
                    value={testimonial.quote}
                    onChange={(e) => handleTestimonialChange(index, 'quote', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    placeholder="Client testimonial quote..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Process Steps Editor */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800 flex items-center">
            <i className="ri-roadmap-line mr-2 text-orange-500"></i>
            Process Steps ({processSteps.length} items)
          </h3>
          <button
            onClick={addProcessStep}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-300 flex items-center whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            Add Step
          </button>
        </div>
        <div className="space-y-6">
          {processSteps.map((step, index) => (
            <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-700">Step {index + 1}</h4>
                <button
                  onClick={() => removeProcessStep(index)}
                  className="text-red-500 hover:text-red-700 transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Step Number</label>
                  <input
                    type="text"
                    value={step.step}
                    onChange={(e) => handleProcessStepChange(index, 'step', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="01"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Duration</label>
                  <input
                    type="text"
                    value={step.duration}
                    onChange={(e) => handleProcessStepChange(index, 'duration', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Day 1"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Title</label>
                  <input
                    type="text"
                    value={step.title}
                    onChange={(e) => handleProcessStepChange(index, 'title', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Discovery Call"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Description</label>
                  <textarea
                    value={step.description}
                    onChange={(e) => handleProcessStepChange(index, 'description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    placeholder="Step description..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Items Editor */}
      <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-slate-800 flex items-center">
            <i className="ri-question-line mr-2 text-indigo-500"></i>
            FAQ Items ({faqs.length} items)
          </h3>
          <button
            onClick={addFaq}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-300 flex items-center whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            Add FAQ
          </button>
        </div>
        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-slate-700">FAQ {index + 1}</h4>
                <button
                  onClick={() => removeFaq(index)}
                  className="text-red-500 hover:text-red-700 transition-colors duration-300 cursor-pointer"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Question</label>
                  <input
                    type="text"
                    value={faq.question}
                    onChange={(e) => handleFaqChange(index, 'question', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="How long does it take to build a law firm website?"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-medium mb-2">Answer</label>
                  <textarea
                    value={faq.answer}
                    onChange={(e) => handleFaqChange(index, 'answer', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                    placeholder="Answer to the question..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
    </div>
  );
}
